import { useState, useEffect } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { ClipboardIcon, CheckIcon, ChevronDownIcon } from 'lucide-react'

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useCurrentUser } from '@/features/auth/state/auth'

import { formatTel } from '../../../components/form/input/tel-mask-input'
import { formatNome, formatCNPJ, limparMascara } from '@/utils/format'

import { useUpdateClientStatus } from '../services/useUpdateClientStatus'
import { formatCPF } from '@/components/form/input/cpf-mask-input'
import ClientStatusTag from './client-status-tag'
import { getStatusColor } from '@/features/products/utils'

const initialClientData = {
  razao_social: '',
  cnpjcpf: '',
  inscricaoEstadual: '',
  nomeProprietario: '',
  cpfProprietario: '',
  logradouro: '',
  numero: '',
  estado: '',
  bairro: '',
  municipio: '',
  emailProprietario: '',
  contatoProprietario: '',
  status: ''
}

type ClientDataProps = {
  clientData: typeof initialClientData
}

export default function DadosCliente({ clientData: initialClientData }: ClientDataProps) {
  const [clientData, setClientData] = useState(initialClientData)
  const [isMobile, setIsMobile] = useState(false)
  const [copiedFields, setCopiedFields] = useState<{ [key: string]: boolean }>({})
  const [expanded, setExpanded] = useState(false)
  const handleCopy = (field: string) => {
    setCopiedFields({ ...copiedFields, [field]: true })
    setTimeout(() => {
      setCopiedFields({ ...copiedFields, [field]: false })
    }, 2000)
  }
  const user = useCurrentUser()
  const updateClientMutation = useUpdateClientStatus()

  const handleStatusChange = async (newStatus: string) => {
    try {
      // Atualiza o estado local para refletir imediatamente no frontend
      setClientData(prevState => ({ ...prevState, status: newStatus }))

      // Atualiza no backend
      if (user) {
        await updateClientMutation.mutateAsync({
          id_client: clientData.cnpjcpf,
          update: { ...clientData, status: newStatus } // Atualiza apenas o status no backend
        })
        console.log('Status atualizado com sucesso no banco:', newStatus)
      }
    } catch (error) {
      console.error('Erro ao atualizar status no banco:', error)
    }
  }

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 640)
    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  const renderField = (label: string, valorFormatado: string | null | undefined, field: string, valorPuro?: string) => {
    if (!valorFormatado) return null
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center space-x-2 px-2 py-1 w-full sm:w-auto min-w-0">
              <span className="font-medium text-xs text-muted-foreground whitespace-nowrap">{label}:</span>
              <span className="text-sm truncate max-w-[150px] sm:max-w-[250px]">{valorFormatado}</span>
              <CopyToClipboard text={valorPuro || valorFormatado} onCopy={() => handleCopy(field)}>
                <button className="ml-1">
                  {copiedFields[field] ? (
                    <CheckIcon className="h-3 w-3 text-green-500" />
                  ) : (
                    <ClipboardIcon className="h-3 w-3 text-muted-foreground" />
                  )}
                </button>
              </CopyToClipboard>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="break-words max-w-xs">{valorFormatado}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  if (!clientData) {
    return (
      <div className="w-full flex items-center justify-center h-screen">
        <span className="text-muted-foreground">Carregando dados do cliente...</span>
      </div>
    )
  }

  return (
    <div className="max-w-full">
      <div className="flex flex-col sm:flex-row items-center sm:justify-between">
        {isMobile ? (
          <div className="w-full flex flex-col items-center px-4 py-2 min-w-0">
            {/* cabeçalho clicável, ocupa toda a largura e centralizado */}
            <h2
              onClick={() => setExpanded(e => !e)}
              className="w-screen text-center text-lg font-semibold cursor-pointer
                        whitespace-nowrap overflow-hidden text-ellipsis max-w-80"
            >
              {clientData.razao_social || 'N/A'}
            </h2>
            {/* detalhes só se expandido, agora em coluna para não estourar width */}
            {expanded && (
              <div className="w-full flex flex-col items-center gap-2 mt-2 min-w-0">
                {renderField('CNPJ', formatCNPJ(clientData.cnpjcpf), 'cnpjcpf', limparMascara(clientData.cnpjcpf))}
                {renderField('Proprietário', formatNome(clientData.nomeProprietario), 'nomeProprietario')}
                {renderField(
                  'CPF',
                  formatCPF(clientData.cpfProprietario),
                  'cpfProprietario',
                  limparMascara(clientData.cpfProprietario)
                )}
                {renderField('E-mail', clientData.emailProprietario, 'emailProprietario')}
                {renderField(
                  'Contato',
                  formatTel(clientData.contatoProprietario),
                  'contatoProprietario',
                  limparMascara(clientData.contatoProprietario)
                )}
              </div>
            )}
          </div>
        ) : (
          <>
            <h2 className="text-lg font-semibold truncate max-w-full">{clientData.razao_social || 'N/A'}</h2>
            <div className="ml-auto flex flex-wrap items-center justify-start w-full max-w-full">
              {renderField('Proprietário', formatNome(clientData?.nomeProprietario), 'nomeProprietario')}
              {renderField(
                'CPF',
                formatCPF(clientData.cpfProprietario),
                'cpfProprietario',
                limparMascara(clientData.cpfProprietario)
              )}
              {renderField('CNPJ', formatCNPJ(clientData?.cnpjcpf), 'cnpjcpf', limparMascara(clientData.cnpjcpf))}
              {renderField('E-mail', clientData?.emailProprietario, 'emailProprietario')}
              {renderField(
                'Contato',
                formatTel(clientData?.contatoProprietario),
                'contatoProprietario',
                limparMascara(clientData.contatoProprietario)
              )}
            </div>
          </>
        )}

        {user?.typeUser === 'ADMIN' ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`flex items-center space-x-1 text-sm px-3 py-1 rounded-full ${getStatusColor(clientData.status)}`}
              >
                <ClientStatusTag status={clientData.status} />
                <ChevronDownIcon className="h-4 w-4 text-white " />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleStatusChange('ATIVO')}>Ativo</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('INADIMPLENTE')}>Inadimplente</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('INATIVO')}>Inativo</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('NOVO')}>Novo</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('JURIDICO')}>Jurídico</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('SEM_OPORTUNIDADE')}>
                Sem Oportunidade
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('PENDENTE')}>Pendente</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('PROCURACAO_CANCELADA')}>
                Procuração Cancelada
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleStatusChange('PROCURACAO_EXPIRADA')}>
                Procuração Expirada
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span
            className={`flex items-center space-x-1 text-sm px-3 py-1 rounded-full cursor-not-allowed ${getStatusColor(clientData.status)}`}
          >
            <ClientStatusTag status={clientData.status} />
          </span>
        )}
      </div>
    </div>
  )
}
